<template>
  <v-card>
    <v-card-title>Forests</v-card-title>
    <v-card-subtitle>
      <v-row>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="search"
            append-icon="search"
            label="Search by name or slug"
            single-line
            hide-details
          ></v-text-field>
        </v-col>
      </v-row>
    </v-card-subtitle>

    <v-data-table
      :headers="headers"
      :items="forests"
      :server-items-length="totalAmount"
      class="elevation-1"
      :loading="loading"
      :footer-props="{
        'items-per-page-options': $options.DEFAULT_ITEMS_PER_PAGE_OPTIONS,
      }"
      fixed-header
      :options.sync="options"
    >
      <template v-slot:[`item.name`]="{ item }">
        <v-menu
          v-model="item.showForestSelector"
          :close-on-content-click="false"
          offset-y
        >
          <template v-slot:activator="{ on }">
            <span v-on="on" style="cursor: pointer; text-decoration: underline">
              <span v-if="item.name && item.name.length > 20">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <span v-on="on">{{ truncateText(item.name) }}</span>
                  </template>
                  <span>{{ item.name }}</span>
                </v-tooltip>
              </span>
              <span v-else>
                {{ item.name }}
              </span>
            </span>
          </template>
          <v-list dense>
            <v-list-item
              :to="{ name: 'Forest', params: { id: item._id } }"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Open Forest
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              @click="copyForestId(item, 'Forest ID', item.id)"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Copy Forest ID
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:[`item.slug`]="{ item }">
        <v-menu
          v-model="item.showSlugSelector"
          :close-on-content-click="false"
          offset-y
        >
          <template v-slot:activator="{ on }">
            <span v-on="on" style="cursor: pointer; text-decoration: underline">
              <span v-if="item.slug && item.slug.length > 20">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <span v-on="on">{{ truncateText(item.slug) }}</span>
                  </template>
                  <span>{{ item.slug }}</span>
                </v-tooltip>
              </span>
              <span v-else>
                {{ item.slug }}
              </span>
            </span>
          </template>
          <v-list dense>
            <v-list-item style="cursor: pointer">
              <v-list-item-title class="font-weight-medium">
                <a
                  :href="`${$options.FOREST_DOMAIN}/${item.slug}`"
                  target="_blank"
                  style="text-decoration: none"
                >
                  Go to Forest Page 🔗
                </a>
                <v-icon>mdi-open-in-new</v-icon>
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              @click="copyForestSlug(item, 'Slug', item.slug)"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Copy Slug
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:[`item.platforms`]="{ item }">
        <platforms-logo :platforms="item.platformsOrder"></platforms-logo>
      </template>
      <template v-slot:[`item.reviewAmount`]="{ item }">
        {{ item.reviewAmount || 0 }}
      </template>
      <template v-slot:[`item.reviewAmountOnPlatform`]="{ item }">
        {{ item.reviewAmountOnPlatform || 0 }}
      </template>
      <template v-slot:[`item.projectId`]="{ item }">
        <span v-if="item.projectId">
          {{
            $options.PROJECT_TYPES.find(
              (p) => Number(p.id) === Number(item.projectId)
            ).name
          }}
        </span>
      </template>
      <template v-slot:[`item.active`]="{ item }">
        {{ item.active ? "Active" : "Deactivated" }}
      </template>
      <template v-slot:[`item.createdAt`]="{ item }">
        {{ formatDate(item.createdAt) }}
      </template>
    </v-data-table>
    <success-snackbar v-model="showSuccessMessage" :message="successMessage" />
  </v-card>
</template>
<script>
import { API } from "../../api";
import {
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_ITEMS_PER_PAGE_OPTIONS,
  FOREST_TYPES,
  PROJECT_TYPES,
} from "../../constants";
import { formatDate, truncateText } from "../../helpers";
import { writeToClipboard } from "../../utils/copyText";
import PlatformsLogo from "../Base/PlatformsLogo.vue";
import SuccessSnackbar from "../Base/SuccessSnackbar.vue";

export default {
  name: "CompanyForests",
  DEFAULT_ITEMS_PER_PAGE_OPTIONS,
  FOREST_TYPES,
  PROJECT_TYPES,
  FOREST_DOMAIN: process.env.VUE_APP_FOREST_DOMAIN,
  components: {
    PlatformsLogo,
    SuccessSnackbar,
  },
  props: {
    companyId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      formatDate,
      truncateText,
      loading: true,
      forests: [],
      search: "",
      showSuccessMessage: false,
      successMessage: "",
      headers: [
        {
          text: "Name",
          align: "start",
          sortable: true,
          value: "name",
          width: "200",
        },
        {
          text: "Slug",
          sortable: true,
          value: "slug",
          width: "150",
        },
        {
          text: "Type",
          sortable: true,
          value: "type",
        },
        {
          text: "Platforms",
          sortable: false,
          value: "platforms",
        },
        {
          text: "Trees Planted",
          sortable: true,
          value: "totalTreeAmount",
          default: 0,
        },
        {
          text: "Score",
          sortable: true,
          value: "score",
          width: "25",
        },
        {
          text: "Review amount",
          sortable: true,
          value: "reviewAmount",
          default: 0,
          width: "25",
        },
        {
          text: "Review amount on platform",
          sortable: true,
          value: "reviewAmountOnPlatform",
          default: 0,
          width: "25",
        },
        {
          text: "Planting Project",
          sortable: true,
          value: "projectId",
        },
        {
          text: "Status",
          sortable: true,
          value: "active",
        },
        {
          text: "Created at",
          sortable: true,
          value: "createdAt",
          width: "200",
        },
      ],
      options: {
        page: 1,
        itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
        sortBy: ["createdAt"],
        sortDesc: [true],
        groupBy: [],
        groupDesc: [],
        mustSort: false,
        multiSort: false,
      },
      totalAmount: 0,
    };
  },
  computed: {
    searchQuery() {
      const trimmedSearch = this.search.trim();
      return trimmedSearch === "" ? null : trimmedSearch;
    },
  },
  watch: {
    companyId: {
      handler: "getForests",
    },
    search: {
      handler: "debounceFetching",
    },
    options: {
      handler: "debounceFetching",
      deep: true,
    },
  },
  methods: {
    debounceFetching() {
      if (this.timerId) {
        clearTimeout(this.timerId);
      }

      this.timerId = setTimeout(() => {
        this.getForests();
      }, 500);
    },
    async getForests() {
      if (!this.companyId) {
        this.loading = false;
        return;
      }

      if (this.companyId) {
        this.forests = [];
      }

      this.loading = true;
      try {
        const forests = await API.getForests({
          ownedBy: this.companyId,
          sortBy: this.options.sortBy[0],
          order: this.options.sortDesc[0] ? "desc" : "asc",
          page: this.options.page,
          pageSize:
            this.options.itemsPerPage === -1 ? 0 : this.options.itemsPerPage,
          query: this.searchQuery,
        });
        this.forests = forests.data;
        this.totalAmount = forests.count;
        this.loading = false;
      } catch (error) {
        this.loading = false;
      }
    },
    async copyToClipboard(label, value, key) {
      try {
        await writeToClipboard(String(value));
        this.copiedKey = key;
        // Show success message
        this.successMessage = `${label} successfully copied to clipboard!`;
        this.showSuccessMessage = true;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to copy " + label, e);
      } finally {
        clearTimeout(this.resetTimer);
        this.resetTimer = setTimeout(() => (this.copiedKey = null), 1500);
      }
    },
    copyForestId(item, type, value) {
      item.showForestSelector = false;
      this.copyToClipboard(type, value, type);
    },
    copyForestSlug(item, type, value) {
      item.showSlugSelector = false;
      this.copyToClipboard(type, value, type);
    },
  },
};
</script>
